
import React from 'react';
import { motion } from 'framer-motion';
import { LogIn, Stars, Moon } from 'lucide-react';
import { AppPreviewFloatingBadge } from './AppPreviewFloatingBadge';
import { useIsMobile } from '@/hooks/use-mobile';

export function AppPreview() {
  const isMobile = useIsMobile();
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.6, duration: 0.7 }}
      className="relative z-10"
    >
      <div className="relative">
        {/* Feature badges - only show on desktop */}
        {!isMobile && (
          <>
            <AppPreviewFloatingBadge 
              position="left-top"
              delay={1.3}
              icon={<LogIn className="h-5 w-5" />}
              title="Easy Tracking"
              description="Log sleep patterns in seconds"
            />
            
            <AppPreviewFloatingBadge 
              position="right-middle"
              delay={1.5}
              icon={<Stars className="h-5 w-5" />}
              title="Sleep Trends"
              description="Reveal hidden patterns"
            />
            
            <AppPreviewFloatingBadge 
              position="left-bottom"
              delay={1.7}
              icon={<Moon className="h-5 w-5" />}
              title="Peaceful Sleep"
              description="Better insights, better sleep"
            />
          </>
        )}
        
        {/* Mobile-specific feature badges - more compact positioning */}
        {isMobile && (
          <>
            <AppPreviewFloatingBadge 
              position="left-top-mobile"
              delay={1.3}
              icon={<LogIn className="h-4 w-4" />}
              title="Easy Tracking"
              description="Log sleep patterns in seconds"
            />
            
            <AppPreviewFloatingBadge 
              position="right-middle-mobile"
              delay={1.5}
              icon={<Stars className="h-4 w-4" />}
              title="Sleep Trends"
              description="Reveal hidden patterns"
            />
          </>
        )}
        
        {/* Dashboard Preview */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200 mx-auto max-w-[90%] md:max-w-full"
        >
          <div className="bg-indigo-600 text-white p-3 md:p-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-sm md:text-base">SleepNest Dashboard</h3>
              <div className="flex space-x-2">
                <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-red-400"></div>
                <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-yellow-400"></div>
                <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-400"></div>
              </div>
            </div>
          </div>
          
          <div className="p-3 md:p-5">
            <div className="mb-3 md:mb-4">
              <h4 className="text-xs md:text-sm font-medium text-gray-700 mb-1 md:mb-2">Sleep Windows</h4>
              <div className="bg-gray-100 rounded-lg h-24 md:h-32 p-2 md:p-3">
                <div className="flex h-full items-end space-x-1">
                  {[15, 30, 60, 80, 55, 45, 35, 20, 10, 5, 25, 40].map((height, i) => (
                    <div key={i} style={{ height: `${height}%` }} className="flex-1 bg-indigo-500 rounded-t"></div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 md:gap-3 mb-3 md:mb-4">
              <div className="bg-blue-50 p-2 md:p-3 rounded-lg">
                <div className="text-2xs md:text-xs text-gray-500 mb-0.5 md:mb-1">Average Sleep</div>
                <div className="font-medium text-xs md:text-sm text-blue-700">9.5 hours</div>
              </div>
              <div className="bg-purple-50 p-2 md:p-3 rounded-lg">
                <div className="text-2xs md:text-xs text-gray-500 mb-0.5 md:mb-1">Sleep Quality</div>
                <div className="font-medium text-xs md:text-sm text-purple-700">Excellent</div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-2 md:p-3">
              <div className="text-2xs md:text-xs text-gray-500 mb-1 md:mb-2">Recent Activity</div>
              <div className="space-y-1 md:space-y-2">
                <div className="bg-white p-1.5 md:p-2 rounded border border-gray-100 flex justify-between items-center">
                  <span className="text-2xs md:text-xs">Night Sleep</span>
                  <span className="text-2xs md:text-xs font-medium">8:30 PM - 6:45 AM</span>
                </div>
                <div className="bg-white p-1.5 md:p-2 rounded border border-gray-100 flex justify-between items-center">
                  <span className="text-2xs md:text-xs">Morning Nap</span>
                  <span className="text-2xs md:text-xs font-medium">10:15 AM - 11:30 AM</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
